import React from 'react';
import {Link as RouterLink} from 'react-router-dom';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme} from '@mui/material/styles';

const LinkRouter = props => <Link {...props} component={RouterLink} />;

// Shared breadcrumb renderer. Each app builds its own `items` array; this owns
// the styling: links for intermediate crumbs, a bold non-link final crumb, and
// responsive collapse/wrap on small screens.
//
// Each item: {label, to, key?, disableLink?, bold?}
//   - `to` missing or `disableLink` true  -> rendered as plain text (no link)
//   - the final crumb is bold + non-link by default; pass an explicit `bold`
//     boolean to override which crumb is treated as terminal (e.g. when a
//     trailing crumb is intentionally suppressed upstream).
export default function Breadcrumbs({items = [], sx}) {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    if (!items.length) {
        return null;
    }

    return (
        <MuiBreadcrumbs
            aria-label="breadcrumb"
            maxItems={isSmall ? 2 : undefined}
            sx={{
                // Let the trail wrap to multiple lines when expanded (tapping the
                // collapsed "…"), while minWidth:0 lets the final crumb (often a
                // long date) ellipsize via its noWrap rather than breaking into a
                // one-word-per-line column when space is tight.
                ...(isSmall && {
                    '& .MuiBreadcrumbs-ol': {flexWrap: 'wrap'},
                    '& .MuiBreadcrumbs-li': {minWidth: 0}
                }),
                ...sx
            }}
        >
            {items.map((item, index) => {
                const {label, to, key, disableLink} = item;
                const last = item.bold ?? (index === items.length - 1);
                const itemKey = key ?? to ?? index;

                if (last || disableLink || !to) {
                    return (
                        <Typography
                            key={itemKey}
                            color="textPrimary"
                            noWrap={last}
                            sx={last ? {fontWeight: 600} : undefined}
                        >
                            {label}
                        </Typography>
                    );
                }

                return (
                    <LinkRouter color="inherit" to={to} key={itemKey}>
                        {label}
                    </LinkRouter>
                );
            })}
        </MuiBreadcrumbs>
    );
}
